import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {useToast} from "../../contexts/toast.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {
  FIELD,
  getFields,
  GROUP,
  hasFacilityGraphableFields,
  mergeBuilders
} from "../../utils/builder.js";

// Components
import Modal from "../../components/Modal.js";
import Chart from "../../components/Chart.js";
import Dropdown from "../../components/Dropdown.js";
import SearchSelect from "../../components/SearchSelect.js";
import InputCheck from "../../components/form/InputCheckHotfix.js";
import {
  InputRadio,
  InputRadioGroup,
  InputSelect,
  InputText
} from "../../components/form/FormInputs.js";

// Style
import {border, pad, radius} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {flex, z} from "../../style/components/mixins.js";
import {bp, breakpoint} from "../../style/components/breakpoints.js";
import {
  Abbr,
  Button,
  ButtonLoader,
  Form,
  FormField,
  FormGroupHeading,
  Heading,
  Label,
  Loader,
  NotLoaded,
  Pill,
  scrollbar,
  StickyWrapper,
  Text
} from "../../style/components/general.js";

const AXES_HELP =
  "1 axis plots all selected fields on a shared scale. 2 axes plots two fields on independent left and right scales — useful when units or magnitudes differ. Dual axes requires exactly two fields.";

const dropdownOptions = ["None", "Last 7 Days", "Last 30 Days", "Last 90 Days", "Last Year"];

const schema = {
  name: yup.string().nullable().required("Please provide a label."),
  sourceType: yup.string().nullable().required("Please provide a source type."),
  source: yup.object().when("sourceType", {
    is: "Facility",
    then: () => yup.object().nullable(),
    otherwise: () => yup.object().nullable().required("Please provide a source.")
  }),
  sources: yup.lazy(val =>
    Array.isArray(val)
      ? yup
          .array()
          .of(yup.string())
          .min(1, "Please select a source")
          .required("Please select a source")
      : yup.string().nullable().required("Please select a source")
  ),
  chartType: yup.string().nullable().required("Please provide chart type.")
};

const defaultValues = {
  after: null,
  name: "",
  sourceType: "Checksheet",
  source: "",
  sources: null,
  chartType: "Line",
  axes: "1"
};

const ModalNewChart = ({visible, setVisible}) => {
  const isMounted = useMountedState();

  const {addToast} = useToast();

  const prevSources = useRef([]);

  const {analytics, setAnalytics, allOrder, facility} = useContext(FacilityNavContext);

  const [sourceOptions, setSourceOptions] = useState(null);
  const [sourcesOptions, setSourcesOptions] = useState(null);
  const [chart, setChart] = useState({});
  const [defaultBuilder, setDefaultBuilder] = useState(null);
  const [stateBuilder, setStateBuilder] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [refreshSources, setRefreshSources] = useState(null);
  const [sourceResults, setSourceResults] = useState([]);
  // Loaders
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const form = useForm({
    defaultValues,
    resolver: yupResolver(
      yup.object().shape(
        {
          ...schema,
          after:
            analytics && analytics.length > 0
              ? yup
                  .number()
                  .transform((v, o) => (o === "" ? null : v))
                  .typeError("Please choose where to display the new chart.")
                  .required("Please choose where to display the new chart.")
              : yup.number().nullable()
        },
        [["sources", "sources"]]
      )
    )
  });
  const {handleSubmit, watch, reset, setValue} = form;

  const sourceType = watch("sourceType");
  const stage = watch("stage");
  const source = watch("source");
  const sources = watch("sources");
  const chartType = watch("chartType");
  const graphableFieldCount = sourcesOptions ? Object.keys(sourcesOptions).length : 0;
  const axes = graphableFieldCount >= 2 ? watch("axes") || "1" : "1";

  const {api: apiChecksheets} = useApi("checksheets");
  const {api: apiChecksheetRecords} = useApi("checksheet-records");
  const {api: apiEvents} = useApi("events");
  const {api: apiEventRecords} = useApi("event-records");
  const {api: apiAnalytics} = useApi("facility-analytics", {suppress: {error: true}});

  const showFacilitySource = useMemo(
    () => hasFacilityGraphableFields(facility, ["number", "generated", "range"]),
    [facility]
  );

  const sourceTypeOptions = useMemo(() => {
    const options = ["Checksheet", "Event"];
    if (showFacilitySource) return ["Facility", ...options];
    return options;
  }, [showFacilitySource]);

  useEffect(() => {
    if (!showFacilitySource && sourceType === "Facility") {
      setValue("sourceType", "Checksheet");
    }
  }, [showFacilitySource, sourceType, setValue]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && facility) {
      // Start from inital values when selected
      setSourcesOptions(null);
      setDefaultBuilder(null);
      setChart({});
      setValue("source", null);
      setValue("sources", null);
      setValue("stage", null);

      if (sourceType === "Facility") {
        if (facility.builder?.allIds && facility.builder?.byId) {
          const fields = getFields(facility.builder.allIds, facility.builder.byId, [
            "number",
            "generated",
            "range"
          ]);
          setDefaultBuilder(facility.builder);
          setStateBuilder(null);
          setSourcesOptions(Object.fromEntries(fields.map(({ancestry, name}) => [name, ancestry])));
        } else {
          setSourcesOptions([]);
        }
        setRefreshSources("Facility");
        return;
      }

      const api = sourceType === "Checksheet" ? apiChecksheets : apiEvents;
      const options = sourceType === "Checksheet" ? {withAllStates: true, withBuilder: true} : {};
      api.callGet(null, {...options, facilityId: facility.id}).then(({status, data}) => {
        if (status === 200 && data) {
          setSourceOptions(
            data.map(task => {
              const {name, frequency, group} = task;
              let fullName = frequency?.name
                ? `${frequency.name.toUpperCase()} ${name}`
                : `${name} (optional)`;

              if (group && facility?.builder?.byId) {
                const groupName = facility.builder.byId[group]?.label;
                fullName = groupName ? `${fullName} at ${groupName}` : fullName;
              }

              return {
                ...task,
                name: fullName
              };
            })
          );
          setRefreshSources(sourceType);
        }
      });
    }
  }, [isMounted, apiChecksheets, apiEvents, sourceType, setValue, facility]);

  const stageOptions = useMemo(() => {
    if (!source?.stages?.allIds) return null;
    const {byId, allIds} = source.stages;
    return allIds
      .filter(stageId => byId[stageId])
      .map(stageId => {
        const {name} = byId[stageId];
        return {name: stageId, label: name};
      });
  }, [source]);

  const selectedStage = useMemo(() => {
    if (!stage || !source?.stages?.byId || !source?.stages?.byId[stage]) return null;
    const {byId} = source.stages;
    return byId[stage];
  }, [source, stage]);

  // Set source on dropdown select
  useEffect(() => {
    if (isMounted() && source && sourceOptions) {
      setValue("type", null);
      setValue("stage", null);
    }
  }, [isMounted, source, sourceOptions, setValue]);

  // Get current Checksheet's records to check if there are enough data values
  useEffect(() => {
    if ((sourceType === "Checksheet" || selectedStage) && source && source.id && refreshSources) {
      const api = refreshSources === "Checksheet" ? apiChecksheetRecords : apiEventRecords;
      const typeId = refreshSources === "Checksheet" ? "facilityChecksheetId" : "eventId";
      const params = {
        [typeId]: source.id
      };

      api.callGet(null, params).then(({status}) => {
        if (status === 200) {
          let fields;

          if (selectedStage?.builder) {
            const {allIds, byId} = selectedStage.builder;
            fields = getFields(allIds, byId, ["number", "generated", "range"]);
            setDefaultBuilder(selectedStage.builder);
          } else {
            const dBuilder = mergeBuilders(
              source.default?.builder || source.builder,
              facility.builder
            );

            const vBuilder = source.state
              ? mergeBuilders(source.state.builder, facility.builder)
              : null;

            const defaultFields = getFields(dBuilder.allIds, dBuilder.byId, [
              "number",
              "generated",
              "range"
            ]);

            const stateFields = vBuilder
              ? getFields(vBuilder.allIds, vBuilder.byId, ["number", "generated", "range"])
              : [];

            const defaultOptions = defaultFields
              .filter(option => {
                const match = stateFields.find(({name}) => name === option.name);

                return (!match || match.deleted) && !option.deleted;
              })
              .map(option => ({
                ...option,
                label: `${option.ancestry} (DEFAULT ONLY)`
              }));

            const bothOptions = defaultFields.filter(option => {
              const match = stateFields.find(({name}) => name === option.name);

              return match && !match.deleted && !option.deleted;
            });

            const stateOptions = stateFields
              .filter(option => {
                const match = defaultFields.find(({name}) => name === option.name);

                return (!match || match.deleted) && !option.deleted;
              })
              .map(option => ({
                ...option,
                label: `${option.ancestry} (${
                  source?.state?.state?.name ? source.state.state.name.toUpperCase() : "STATE"
                } ONLY)`
              }));

            fields = [...bothOptions, ...defaultOptions, ...stateOptions];

            setDefaultBuilder(dBuilder);
            setStateBuilder(vBuilder);
          }

          if (fields) {
            setValue("sources", []);
            const options = Object.fromEntries(fields.map(({ancestry, name}) => [name, ancestry]));

            setSourcesOptions(options);
          } else setSourcesOptions([]);
        }
      });
    }
  }, [
    setValue,
    source,
    refreshSources,
    apiChecksheetRecords,
    apiEventRecords,
    setSourcesOptions,
    selectedStage,
    sourceType,
    facility
  ]);

  // Allow Multiple Sources
  useEffect(() => {
    if (
      axes === "2" &&
      sources &&
      sources.length > 2 &&
      prevSources.current &&
      prevSources.current.length === 2
    ) {
      addToast("Two sources maximum for two axis graph.");
      setValue("sources", prevSources.current);
    } else if (axes === "2" && Array.isArray(sources) && sources && sources.length > 2) {
      addToast("Two sources maximum for two axis graph.");
      setValue("sources", [sources[0], sources[1]]);
      prevSources.current = [sources[0], sources[1]];
    }
    if (sources) {
      prevSources.current = sources;
    }
  }, [axes, sources, setValue, addToast]);

  // Preview Chart
  useEffect(() => {
    const hasSource = sourceType === "Facility" || source;
    if (
      isMounted() &&
      sourcesOptions &&
      refreshSources &&
      hasSource &&
      sources &&
      sources.length > 0 &&
      !(axes === "2" && sources.length > 2)
    ) {
      setPreviewLoading(true);

      const params = {
        preview: true,
        type: "plot",
        sources: typeof sources === "string" ? [sources] : sources,
        snapshot: snapshot || "none",
        axes: axes ? parseInt(axes, 10) : 1
      };

      if (sourceType === "Facility") {
        params.facilityId = facility.id;
      } else {
        const typeId = refreshSources === "Checksheet" ? "facilityChecksheetId" : "eventId";
        params[typeId] = source.id;
        if (sourceType === "Event") params.stageId = stage;
      }

      apiAnalytics
        .callPost(params)
        .then(({status, data}) => {
          if (status === 200 && data) setChart(data);
        })
        .finally(() => setPreviewLoading(false));
    }

    if (!sources || sources.length === 0 || (axes === "2" && sources && sources.length > 2))
      setChart({});
  }, [
    isMounted,
    apiAnalytics,
    sources,
    snapshot,
    axes,
    refreshSources,
    source,
    sourcesOptions,
    stage,
    sourceType,
    facility
  ]);

  const addStat = data => {
    if (sources) {
      setSubmitLoading(true);

      let updated = 1;
      const order = data.after;
      if (order) updated = order + 1;

      const params = {
        type: "plot",
        label: data.name,
        sources: typeof sources === "string" ? [sources] : sources,
        order: updated,
        snapshot: snapshot || "none",
        axes: axes ? parseInt(axes, 10) || 1 : 1,
        chartType
      };

      if (sourceType === "Facility") {
        params.facilityId = facility.id;
      } else {
        const typeId = sourceType === "Checksheet" ? "facilityChecksheetId" : "eventId";
        params[typeId] = source.id;
        if (sourceType === "Event") params.stageId = stage;
      }

      apiAnalytics.callPost(params).then(({status}) => {
        if (status === 201) {
          setAnalytics(null);
          setSubmitLoading(false);
          reset(defaultValues);
        }
      });
    }
  };

  useEffect(() => {
    if (!source) {
      setSourcesOptions(null);
      setDefaultBuilder(null);
    }
  }, [source]);

  useEffect(() => {
    if (!visible) reset(defaultValues);
  }, [reset, visible, isMounted]);

  useEffect(() => {
    setValue("axes", "1");
    setValue("sources", []);
  }, [chartType, setValue]);

  const searchSources = query => {
    if (!query) setSourceResults(sourceOptions);
    else
      setSourceResults(
        sourceOptions.filter(s => s.name.toLowerCase().includes(query.toLowerCase()))
      );
  };

  const recurseLayout = (InputComponent, ids, b, rendered = {}) =>
    ids.map(id => {
      if (rendered[id]) return null;

      const item = b.byId[id];
      if (!item || item.deleted || item.stale || (item.element === FIELD && !sourcesOptions[id]))
        return null;

      const {children} = item;
      const renderedChildren = children?.length
        ? recurseLayout(InputComponent, children, b, rendered)
        : [];

      if (
        item.element !== FIELD &&
        (!renderedChildren.length || renderedChildren.every(child => !child))
      )
        return null;

      rendered[id] = true;

      if (item.element === FIELD) {
        let stateLabel = "";
        if (item.state && item.state !== "both")
          stateLabel =
            item.state === "default"
              ? " (DEFAULT ONLY)"
              : ` (${
                  source.state?.state?.name ? source.state.state.name.toUpperCase() : "STATE"
                } ONLY)`;

        return (
          <InputComponent key={id} value={id} name="sources">
            {item.label}
            {stateLabel}
          </InputComponent>
        );
      }

      return (
        <ElementWrapper key={id} element={item.element}>
          <ElementHeading element={item.element} data-testid={`element-${id}`}>
            {item.label}
          </ElementHeading>
          {renderedChildren}
        </ElementWrapper>
      );
    });

  const mergeInState = (dBuilder, vBuilder, ids, consolidated = {allIds: [], byId: {}}) => {
    const filtered = ids.filter(id => !consolidated.allIds.includes(id) && !consolidated.byId[id]);
    consolidated.allIds.push(...filtered);
    filtered.map(name => {
      let item = dBuilder.byId[name];
      const children = [];

      let whichState = "both";
      const vItem = vBuilder.byId[name];
      const dItem = dBuilder.byId[name];
      const vExists = vItem && !vItem.deleted && !vItem.stale;
      const dExists = dItem && !dItem.deleted && !dItem.stale;

      if (dItem && dItem.children) children.push(...dItem.children);
      if (vItem && vItem.children)
        children.push(...vItem.children.filter(id => !children.includes(id)));
      if (vExists && !dExists) {
        whichState = "state";
        item = vItem;
      } else if (dExists && !vExists) {
        whichState = "default";
        item = dItem;
      }

      if (item) {
        if (!(name in consolidated.byId))
          consolidated.byId[name] = {...item, state: whichState, children};

        if (children?.length) mergeInState(dBuilder, vBuilder, children, consolidated);
      }
    });

    return consolidated;
  };

  const buildLayout = InputComponent => {
    let mergedWithState = defaultBuilder;

    if (stateBuilder) {
      mergedWithState = mergeInState(defaultBuilder, stateBuilder, [
        ...defaultBuilder.allIds,
        ...stateBuilder.allIds.filter(id => !defaultBuilder.allIds.includes(id))
      ]);
    }

    const main = recurseLayout(InputComponent, mergedWithState.allIds, mergedWithState);
    return main;
  };

  return (
    <Modal visible={visible} setVisible={setVisible} maxWidth={breakpoint.width[5]}>
      <ModalTitle>Create Chart</ModalTitle>

      <StyledText>
        Create a chart from <strong>number</strong> fields on task submissions.
      </StyledText>

      <FormProvider {...form}>
        <Form data-testid="chartForm" onSubmit={handleSubmit(addStat)} noValidate>
          <Row>
            <Left>
              <InputRadioGroup
                name="chartType"
                label="CHART TYPE"
                options={["Line", "Bar", "Scatter"]}
              />

              <FormField standard>
                <InputRadioGroup
                  name="sourceType"
                  options={sourceTypeOptions}
                  defaultValue="Checksheet"
                  label="SOURCE TYPE"
                />
              </FormField>
              {sourceType !== "Facility" && (
                <SearchSelectWrapper>
                  {!source ? (
                    <SearchSelect
                      label="SOURCE"
                      placeholder={`Select ${sourceType}...`}
                      results={sourceResults}
                      setResults={setSourceResults}
                      search={searchSources}
                      add={val => setValue("source", val)}
                      showAll
                    />
                  ) : (
                    <>
                      <Label bold>SOURCE</Label>
                      <SourceContainer data-testid="addField.units-selected">
                        <Abbr title={source.name}>{source.name}</Abbr>
                        &nbsp;
                        <IconButton
                          onClick={() => {
                            setValue("source", null);
                            setValue("sources", null);
                            setValue("stage", null);
                          }}>
                          <FontAwesomeIcon icon={faClose} />
                        </IconButton>
                      </SourceContainer>
                    </>
                  )}
                </SearchSelectWrapper>
              )}

              {sourceType === "Event" && source && (
                <FormField>
                  <InputSelect
                    name="stage"
                    placeholder="Select Stage..."
                    options={stageOptions}
                    label="STAGE"
                  />
                </FormField>
              )}

              {((sourceType === "Facility" && defaultBuilder) ||
                ((sourceType !== "Event" || !!stage) && source)) && (
                <FieldOptionsWrapper>
                  {sourcesOptions === null ? (
                    <NotLoaded>
                      <Loader />
                    </NotLoaded>
                  ) : (
                    <>
                      {(chartType === "Line" || chartType === "Scatter") &&
                        graphableFieldCount >= 2 && (
                          <FormField standard>
                            <InputRadioGroup
                              testId="chart-axes"
                              name="axes"
                              options={["1", "2"]}
                              defaultValue="1"
                              label="AXES"
                              help={AXES_HELP}
                            />
                          </FormField>
                        )}
                      {graphableFieldCount > 0 ? (
                        <FormField standard>
                          <Label htmlFor="sources" bold>
                            SELECT FIELD{chartType === "Bar" ? "" : "S"}
                          </Label>
                          <FieldList data-testid="chart-field-list">
                            {chartType === "Line" || chartType === "Scatter"
                              ? buildLayout(InputCheck)
                              : buildLayout(InputRadio)}
                          </FieldList>
                        </FormField>
                      ) : (
                        <None data-testid="noDataMessage">
                          {source && sourcesOptions && (
                            <span>
                              Selected source has no graphable data points. Checksheet must have a
                              number field.
                            </span>
                          )}
                        </None>
                      )}
                    </>
                  )}
                </FieldOptionsWrapper>
              )}
              <TextWrapper>
                <InputText
                  testId="plotLabel"
                  name="name"
                  placeholder="Enter Label..."
                  label="LABEL"
                />
              </TextWrapper>
              {analytics?.length > 0 && (
                <FormField>
                  <InputSelect
                    name="after"
                    label="Display After"
                    placeholder="Select..."
                    options={[{label: "Top of Page", value: "0"}, ...allOrder]}
                    fullWidth
                  />
                </FormField>
              )}
            </Left>
            <StickyDiv>
              <Chart
                testId="plot"
                loading={previewLoading ? 1 : 0}
                label={watch("name")}
                chart={chart}
                view="builder"
                allSources={sourcesOptions ? Object.keys(sourcesOptions) : []}
                chartType={chartType}
              />
              {!(chart && chart.sources && chart.sources.length > 0) && (
                <StyledWarning>{chart.message}</StyledWarning>
              )}

              <Dropdown
                id="snapshot"
                testId="snapshotDropdown"
                name="snapshot"
                options={dropdownOptions}
                placeholder="Date Range..."
                setSelection={setSnapshot}
                selection={snapshot || ""}
                fullWidth
                spaced
              />
            </StickyDiv>
          </Row>
          <Button type="submit" loading={submitLoading ? 1 : 0}>
            {chart.message ? "Create as Template" : "Create"}
            {submitLoading && <ButtonLoader />}
          </Button>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalNewChart.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired
};

// Style Overrides
const ModalTitle = styled(Heading)`
  margin: ${pad}px 0;
  text-align: center;
  font-weight: 700;
`;

const Row = styled.div`
  position: relative;
  width: 100%;

  ${bp(2)} {
    ${flex("row", "nowrap", "start", "start")};
  }
`;

const Left = styled.section`
  ${bp(2)} {
    width: 40%;
    padding-right: ${pad}px;
  }
`;

const StyledText = styled(Text)`
  margin: 0 0 ${pad}px;
  max-width: 700px;
`;

const StyledWarning = styled(Text)`
  margin: ${pad}px 0;
  color: red;
`;

const None = styled(Text)`
  margin: ${pad}px 0 ${pad * 2}px;
  ${({theme}) => css`
    color: ${theme.error};
  `}
`;

const StickyDiv = styled(StickyWrapper)`
  width: 100%;
  padding: ${pad}px;

  ${bp(2)} {
    width: 60%;
    left: 40%;
    top: ${pad * 4}px;
    z-index: ${z("above")};
  }
`;

const FieldOptionsWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${pad}px;
`;

const FieldList = styled.div`
  max-height: 32vh;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: ${pad / 2}px;
  padding: ${pad / 2}px ${pad}px ${pad / 2}px ${pad / 2}px;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  ${scrollbar};
`;

const SourceContainer = styled(Pill)`
  width: min-content;
  height: min-content;
  max-width: 100%;
  margin: ${pad}px ${pad}px 0 0;
  color: ${({theme}) => theme.tertiary};
`;

const IconButton = styled(Button)`
  ${voice.quiet};
  background-color: transparent;
  width: min-content;
  padding: 0;

  svg {
    fill: ${({theme}) => theme.tertiary};
  }
`;

const TextWrapper = styled.div`
  max-width: 200px;
  margin-bottom: ${pad}px;
`;

const SearchSelectWrapper = styled.div`
  max-width: 200px;
  width: 200px;
  margin-bottom: ${pad}px;

  input {
    max-width: 200px !important;
    width: 200px;
  }
`;

const ElementHeading = styled(FormGroupHeading)`
  ${voice.normal}
  color: ${({theme, element}) => theme[element]};

  margin-top: ${pad / 2}px;
`;

const ElementWrapper = styled.div`
  width: 100%;
  > div {
    margin-left: ${pad}px;
    width: 100%;
  }

  margin-bottom: ${({element}) => (element === GROUP ? pad : 0)}px;
`;

export default ModalNewChart;
